import React from "react";
import toast from "react-hot-toast";

import CourierService from "@/services/CourierService";

interface AddZoneModalProps {
  onClose: () => void;
  onSubmit: (data: any) => void;
  open: boolean;
}

const AddZoneModal: React.FC<AddZoneModalProps> = ({
  onClose,
  onSubmit,
  open,
}) => {
  const [activeTab, setActiveTab] = React.useState<"subzone" | "zone">("zone");

  // Zone fields
  const [name, setName] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [loading, setLoading] = React.useState(false);

  // Subzone fields
  const [subzoneName, setSubzoneName] = React.useState("");
  const [subzoneDescription, setSubzoneDescription] = React.useState("");
  const [zoneList, setZoneList] = React.useState<any[]>([]);
  const [selectedZoneId, setSelectedZoneId] = React.useState("");
  const [latitude, setLatitude] = React.useState("");
  const [longitude, setLongitude] = React.useState("");
  const [reason, setReason] = React.useState("");

  // Validation helpers
  const isLatitudeValid = (lat: string) => {
    const n = Number(lat);
    return lat !== "" && !isNaN(n) && n >= -90 && n <= 90;
  };
  const isLongitudeValid = (lng: string) => {
    const n = Number(lng);
    return lng !== "" && !isNaN(n) && n >= -180 && n <= 180;
  };

  const isZoneFormValid = name.trim() !== "" && description.trim() !== "";
  const isSubzoneFormValid =
    selectedZoneId &&
    subzoneName.trim() !== "" &&
    subzoneDescription.trim() !== "" &&
    isLatitudeValid(latitude) &&
    isLongitudeValid(longitude) &&
    reason.trim() !== "";

  // Fetch zones for subzone tab
  React.useEffect(() => {
    if (open && activeTab === "subzone") {
      CourierService.getServiceZones().then((zones) => {
        setZoneList(zones || []);
      });
    }
  }, [open, activeTab]);

  const handleZoneSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const zone = await CourierService.createServiceZone({
        description,
        name,
      });
      if (zone) {
        toast.success("Zone added successfully");
        onSubmit(zone);
        setName("");
        setDescription("");
        onClose();
      } else {
        toast.error("Failed to add zone");
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Error adding zone");
    } finally {
      setLoading(false);
    }
  };

  const handleSubzoneSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedZoneId) {
      toast.error("Select a zone first");
      return;
    }
    if (!latitude || !longitude || !reason) {
      toast.error("Latitude, longitude, and reason are required");
      return;
    }
    setLoading(true);
    try {
      const subzone = await CourierService.createServiceSubzone({
        description: subzoneDescription,
        latitude: parseFloat(latitude),
        longitude: parseFloat(longitude),
        name: subzoneName,
        reason,
        zone_id: selectedZoneId,
      });
      if (subzone) {
        toast.success("Subzone added successfully");
        setSubzoneName("");
        setSubzoneDescription("");
        setLatitude("");
        setLongitude("");
        setReason("");
        onClose();
      } else {
        toast.error("Failed to add subzone");
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Error adding subzone");
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="w-full max-w-md rounded-lg bg-white p-6 shadow-lg dark:bg-gray-900"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Nav Tabs */}
        <div className="mb-6 flex border-b">
          <button
            className={`px-4 py-2 font-semibold ${
              activeTab === "zone"
                ? "border-b-2 border-blue-600 text-blue-600"
                : "text-gray-600"
            }`}
            onClick={() => setActiveTab("zone")}
            type="button"
          >
            Add Zone
          </button>
          <button
            className={`ml-4 px-4 py-2 font-semibold ${
              activeTab === "subzone"
                ? "border-b-2 border-blue-600 text-blue-600"
                : "text-gray-600"
            }`}
            onClick={() => setActiveTab("subzone")}
            type="button"
          >
            Add Subzone
          </button>
        </div>
        {/* Tab Content */}
        {activeTab === "zone" ? (
          <>
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">
              Add Service Zone
            </h2>
            <form onSubmit={handleZoneSubmit}>
              <input
                className="mb-2 w-full rounded border p-2 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                disabled={loading}
                onChange={(e) => setName(e.target.value)}
                placeholder="Zone Name"
                required
                value={name}
              />
              <input
                className="mb-2 w-full rounded border p-2 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                disabled={loading}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Coverage"
                required
                value={description}
              />
              <div className="mt-4 flex justify-end gap-2">
                <button
                  className="rounded bg-gray-200 px-4 py-2 text-gray-700 dark:bg-gray-700 dark:text-gray-200"
                  disabled={loading}
                  onClick={onClose}
                  type="button"
                >
                  Cancel
                </button>
                <button
                  className="rounded bg-blue-600 px-4 py-2 text-white dark:bg-blue-500"
                  disabled={loading || !isZoneFormValid}
                  type="submit"
                >
                  {loading ? "Adding..." : "Add"}
                </button>
              </div>
            </form>
          </>
        ) : (
          <>
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">
              Add Service Subzone
            </h2>
            <form onSubmit={handleSubzoneSubmit}>
              <select
                className="mb-2 w-full rounded border p-2 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                disabled={loading}
                onChange={(e) => setSelectedZoneId(e.target.value)}
                required
                value={selectedZoneId}
              >
                <option value="">Select Zone</option>
                {zoneList.map((zone) => (
                  <option key={zone.id} value={zone.id}>
                    {zone.name}
                  </option>
                ))}
              </select>
              <input
                className="mb-2 w-full rounded border p-2 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                disabled={loading}
                onChange={(e) => setSubzoneName(e.target.value)}
                placeholder="Subzone Name"
                required
                value={subzoneName}
              />
              <input
                className="mb-2 w-full rounded border p-2 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                disabled={loading}
                onChange={(e) => setSubzoneDescription(e.target.value)}
                placeholder="Coverage"
                required
                value={subzoneDescription}
              />
              <input
                className={`mb-2 w-full rounded border p-2 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 ${latitude && !isLatitudeValid(latitude) ? "border-red-500" : ""}`}
                disabled={loading}
                max="90"
                min="-90"
                onChange={(e) => setLatitude(e.target.value)}
                placeholder="Latitude (-90 to 90)"
                required
                step="any"
                type="number"
                value={latitude}
              />
              <input
                className={`mb-2 w-full rounded border p-2 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 ${longitude && !isLongitudeValid(longitude) ? "border-red-500" : ""}`}
                disabled={loading}
                max="180"
                min="-180"
                onChange={(e) => setLongitude(e.target.value)}
                placeholder="Longitude (-180 to 180)"
                required
                step="any"
                type="number"
                value={longitude}
              />
              <input
                className="mb-2 w-full rounded border p-2 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                disabled={loading}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Reason"
                required
                value={reason}
              />
              <div className="mt-4 flex justify-end gap-2">
                <button
                  className="rounded bg-gray-200 px-4 py-2 text-gray-700 dark:bg-gray-700 dark:text-gray-200"
                  disabled={loading}
                  onClick={onClose}
                  type="button"
                >
                  Cancel
                </button>
                <button
                  className="rounded bg-blue-600 px-4 py-2 text-white dark:bg-blue-500"
                  disabled={loading || !isSubzoneFormValid}
                  type="submit"
                >
                  {loading ? "Adding..." : "Add"}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default AddZoneModal;
